import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Inbox, Check, X, User, Clock, Camera, FileText } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-stone-100 text-stone-700",
  approved: "bg-[#E8F0E5] text-[#4A6E42]",
  rejected: "bg-[#FBE9E7] text-[#B4453E]",
  scheduled: "bg-[#D4A373]/20 text-[#B07D4F]",
};

export default function PatientRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/patient-requests");
      setItems(data || []);
    } catch (_e) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function setStatus(id, status) {
    try {
      await api.patch(`/patient-requests/${id}/status`, { status });
      toast.success("Stato aggiornato");
      load();
    } catch (_e) { toast.error("Errore"); }
  }

  return (
    <div className="space-y-6 fade-in-up" data-testid="patient-requests-page">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">Portale paziente</p>
        <h1 className="font-heading text-4xl lg:text-5xl font-light">Richieste dai pazienti</h1>
        <p className="text-stone-500 mt-2">Le richieste inviate dai pazienti dal tuo link pubblico.</p>
      </div>

      {loading ? (
        <p className="text-stone-500">Caricamento…</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-2xl p-12 text-center">
          <Inbox className="w-10 h-10 text-stone-300 mx-auto mb-4" strokeWidth={1.25} />
          <p className="text-stone-500">Nessuna richiesta al momento.</p>
          <p className="text-xs text-stone-400 mt-2">Condividi il tuo link <span className="font-mono">/dr/tuo-slug</span> per iniziare a ricevere richieste.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((r) => (
            <div key={r.id} data-testid={`request-${r.id}`}
              className="bg-white border border-stone-200 rounded-2xl p-6">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
                    <span className="font-medium text-stone-900">{r.patient?.full_name || "—"}</span>
                    <span className="text-xs text-stone-400">· {r.patient?.email || ""}</span>
                    {r.patient?.phone && <span className="text-xs text-stone-400">· {r.patient.phone}</span>}
                  </div>
                  <div className="text-sm text-stone-700">
                    <span className="uppercase text-[10px] tracking-widest text-stone-400 mr-2">Tipo</span>
                    {r.type === "consulta" ? "Consulto" : "Trattamento"}
                    {r.treatment && <> · <span className="text-[#B07D4F]">{r.treatment}</span></>}
                  </div>
                  {r.message && (
                    <div className="mt-3 p-3 bg-[#FAF9F6] rounded-lg text-sm text-stone-700 border-l-2 border-[#D4A373]">
                      &ldquo;{r.message}&rdquo;
                    </div>
                  )}
                  {(r.patient?.allergies || r.patient?.diseases) && (
                    <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs">
                      {r.patient?.allergies && <div><span className="uppercase tracking-widest text-stone-400">Allergie:</span> <span className="text-stone-700">{r.patient.allergies}</span></div>}
                      {r.patient?.diseases && <div><span className="uppercase tracking-widest text-stone-400">Patologie:</span> <span className="text-stone-700">{r.patient.diseases}</span></div>}
                    </div>
                  )}
                  {r.photos?.length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center gap-1 text-xs text-stone-500 mb-2">
                        <Camera className="w-3 h-3" /> {r.photos.length} foto caricate
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {r.photos.map((p, i) => (
                          <a key={i} href={p.url} target="_blank" rel="noreferrer" className="block">
                            <img src={p.url} alt={p.tag} className="w-24 h-24 object-cover rounded-lg border border-stone-200 hover:border-[#D4A373]" />
                            <div className="text-[10px] uppercase tracking-widest text-stone-400 text-center mt-1">{p.tag}</div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-1 text-xs text-stone-400">
                    <Clock className="w-3 h-3" /> {new Date(r.created_at).toLocaleString("it-IT")}
                  </div>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full ${STATUS_STYLES[r.status] || "bg-stone-100 text-stone-500"}`}>
                    {r.status}
                  </span>
                  {r.status === "pending" && (
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => setStatus(r.id, "approved")} data-testid={`approve-${r.id}`}
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#8FA08B] text-white text-xs hover:opacity-90">
                        <Check className="w-3 h-3" /> Approva
                      </button>
                      <button onClick={() => setStatus(r.id, "rejected")} data-testid={`reject-${r.id}`}
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white border border-stone-200 text-stone-700 text-xs hover:bg-stone-50">
                        <X className="w-3 h-3" /> Rifiuta
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
